import { supabase } from "@/integrations/supabase/client";

export type ContractMergeData = {
  reservation: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    check_in: string;
    check_out: string;
    check_in_time?: string | null;
    check_out_time?: string | null;
    guests: number;
    total_price: number;
  };
  client?: {
    address?: string | null;
    postal_code?: string | null;
    city?: string | null;
    country?: string | null;
  } | null;
  quote?: {
    subtotal?: number | null;
    cleaning_fee?: number | null;
    tourist_tax?: number | null;
    discount?: number | null;
    nights?: number | null;
  } | null;
  payments?: Array<{ payment_type: string; amount: number }>;
  securityDeposit?: number;
};

const formatDateFr = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("fr-FR");

const nightsBetween = (a: string, b: string) => {
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
};

const fmtMoney = (n: number | null | undefined) =>
  n === null || n === undefined ? "0" : Number(n).toFixed(2).replace(/\.00$/, "");

/**
 * Replace every {{token}} in a template HTML with real reservation/client data.
 * Unknown tokens are left untouched so the admin can see missing data.
 */
export const applyContractMergeFields = (
  templateHtml: string,
  data: ContractMergeData
): string => {
  const { reservation, client, quote, payments, securityDeposit } = data;

  const nights = quote?.nights ?? nightsBetween(reservation.check_in, reservation.check_out);
  const checkInTime = reservation.check_in_time || "16:00";
  const checkOutTime = reservation.check_out_time || "10:00";

  const depositPayment = payments?.find((p) => p.payment_type === "deposit");
  const finalPayment = payments?.find((p) => p.payment_type === "final");
  const depositAmount = depositPayment
    ? depositPayment.amount
    : Math.round(reservation.total_price * 0.3 * 100) / 100;
  const finalAmount = finalPayment
    ? finalPayment.amount
    : Math.round((reservation.total_price - depositAmount) * 100) / 100;

  const totalPrice = reservation.total_price;
  const cleaningFee = quote?.cleaning_fee ?? 0;
  const touristTax = quote?.tourist_tax ?? 0;
  const discount = quote?.discount ?? 0;
  const subtotal =
    quote?.subtotal ?? Math.max(0, totalPrice - cleaningFee - touristTax + discount);

  const map: Record<string, string> = {
    // identity
    client_name: `${reservation.first_name} ${reservation.last_name}`.trim(),
    first_name: reservation.first_name || "",
    last_name: reservation.last_name || "",
    email: reservation.email || "",
    phone: reservation.phone || "",
    guests: String(reservation.guests ?? ""),
    nights: String(nights),

    // address
    address: client?.address || "",
    postal_code: client?.postal_code || "",
    city: client?.city || "",
    country: client?.country || "France",

    // stay
    check_in: formatDateFr(reservation.check_in),
    check_out: formatDateFr(reservation.check_out),
    check_in_time: checkInTime,
    check_out_time: checkOutTime,
    contract_date: formatDateFr(new Date().toISOString()),
    current_date: formatDateFr(new Date().toISOString()),

    // reservation
    reservation_number: reservation.id.slice(0, 8).toUpperCase(),

    // pricing
    subtotal: fmtMoney(subtotal),
    cleaning_fee: fmtMoney(cleaningFee),
    tourist_tax: fmtMoney(touristTax),
    discount: fmtMoney(discount),
    total_price: fmtMoney(totalPrice),
    deposit_amount: fmtMoney(depositAmount),
    final_amount: fmtMoney(finalAmount),
    security_deposit: fmtMoney(securityDeposit ?? 2000),
  };

  return templateHtml.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (match, key: string) => {
    const value = map[key.toLowerCase()];
    return value !== undefined ? value : match;
  });
};

/**
 * Load quote + payments + client for a reservation, then build merge data.
 */
export const loadContractMergeData = async (reservationId: string): Promise<ContractMergeData | null> => {
  const { data: reservation } = await supabase
    .from("reservations")
    .select("*")
    .eq("id", reservationId)
    .maybeSingle();
  if (!reservation) return null;

  const [{ data: client }, { data: quotes }, { data: payments }] = await Promise.all([
    supabase
      .from("clients")
      .select("address, postal_code, city, country")
      .ilike("email", reservation.email)
      .maybeSingle(),
    supabase
      .from("quotes")
      .select("subtotal, cleaning_fee, tourist_tax, discount, nights, created_at")
      .ilike("email", reservation.email)
      .eq("check_in", reservation.check_in)
      .eq("check_out", reservation.check_out)
      .order("created_at", { ascending: false })
      .limit(1),
    supabase
      .from("payments")
      .select("payment_type, amount")
      .eq("reservation_id", reservationId),
  ]);

  return {
    reservation: reservation as any,
    client: client || null,
    quote: quotes?.[0] || null,
    payments: payments || [],
  };
};
